// WhatsappOptInCard — the D-16 one-time prompt for existing verified-phone users who never gave
// WhatsApp consent. Sits in the bell dropdown and /notifications top slot (46-24).
//
// Renders only when the server says the prompt is eligible (verified number, no consent, not
// dismissed) and there is at least one verified option. Accepting sends an option BY REFERENCE
// (login or claimId), never a phone number: the server resolves the verified number (T-46-24-02).
// Accept records consent with source bell_prompt; dismiss records promptDismissedAt. Either way the
// preferences refetch turns whatsappPromptEligible off, so the card never shows again.
//
// All copy comes from WHATSAPP_OPTIN_COPY (shared), so this file holds no Arabic literals.
//
// Phase 49 (49-15, UI-SPEC S3): the review thank-you reuses this card as the reviewer's single
// second WhatsApp ask, with source "onboarding" and copyVariant "onboarding" (title/body swap only;
// buttons, layout and the by-reference option are unchanged). Defaults keep the bell/inbox card
// byte-for-byte as before.

import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { PREFERENCES_COPY, WHATSAPP_OPTIN_COPY, maskPhone } from "@shared/notificationCopy";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import {
  dismissWhatsappPrompt,
  getNotificationPreferences,
  grantWhatsappConsent,
  type NotificationPreferences,
  type WhatsappConsentOption,
  type WhatsappConsentOptionView,
} from "@/api/notificationService";
import { NOTIFICATION_QUERY_KEYS } from "@/lib/notificationUi";

const font = "var(--ys-font)";

// Split marker so the masked number sits in its own dir="ltr" span inside the RTL sentence.
const SLOT = "\u0000";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r)",
  padding: 16,
  direction: "rtl",
  fontFamily: font,
  textAlign: "start",
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
};

const titleStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  color: "var(--ys-ink-2)",
  margin: "4px 0 0",
};

const acceptBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 16px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--ys-ink-2)",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 12px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

/** Same cache entry PushSoftAsk and the preferences matrix read: the unwrapped preferences. */
export const fetchNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const res = await getNotificationPreferences();
  if (!res.ok) throw new Error(res.error);
  return res.data;
};

/** Login number first (it is the user's own), else the first verified claim number. */
export const pickWhatsappOption = (
  options: WhatsappConsentOptionView[] | undefined,
): WhatsappConsentOptionView | null => {
  if (!options || options.length === 0) return null;
  return options.find((o) => o.kind === "login") ?? options[0];
};

/** True when the D-16 card has something to show. Parents use it to keep one card at a time. */
export const isWhatsappPromptVisible = (prefs: NotificationPreferences | undefined): boolean =>
  !!prefs?.prompts?.whatsappPromptEligible && !!pickWhatsappOption(prefs.whatsappOptions);

/** Strip the view down to the reference the API accepts (no phoneLast3, no phone). */
export const toConsentOption = (view: WhatsappConsentOptionView): WhatsappConsentOption =>
  view.kind === "claim" ? { kind: "claim", claimId: view.claimId } : { kind: "login" };

function CardBody({ phoneMasked, copyVariant }: { phoneMasked: string; copyVariant: CopyVariant }) {
  const template = copyVariant === "onboarding" ? WHATSAPP_OPTIN_COPY.onboardingCardBody : WHATSAPP_OPTIN_COPY.cardBody;
  const [before, after = ""] = template(SLOT).split(SLOT);
  return (
    <p style={bodyStyle}>
      {before}
      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
        {phoneMasked}
      </span>
      {after}
    </p>
  );
}

type CopyVariant = "default" | "onboarding";

type Props = {
  surface: "bell_dropdown" | "inbox_page" | "review_thank_you";
  /** Consent source recorded on accept. "onboarding" is the S3 second ask on the review thank-you. */
  source?: "bell_prompt" | "onboarding";
  copyVariant?: CopyVariant;
  onDone?: () => void;
};

export default function WhatsappOptInCard({ surface, source = "bell_prompt", copyVariant = "default", onDone }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { trackNotifications } = usePostHogTracking();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const prefs = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.preferences,
    queryFn: fetchNotificationPreferences,
    enabled: !!user,
    retry: false,
  });

  const option = pickWhatsappOption(prefs.data?.whatsappOptions);
  if (done || !user || !isWhatsappPromptVisible(prefs.data) || !option) return null;

  const finish = () => {
    setDone(true);
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.preferences });
    onDone?.();
  };

  const handleAccept = async () => {
    setBusy(true);
    const res = await grantWhatsappConsent({ option: toConsentOption(option), source });
    setBusy(false);
    if (!res.ok) {
      toast.error(PREFERENCES_COPY.saveFailedToast);
      return;
    }
    toast.success(WHATSAPP_OPTIN_COPY.successToast);
    trackNotifications.whatsappOptinChanged({
      enabled: true,
      source: source === "onboarding" ? "onboarding_second_ask" : "bell_prompt",
    });
    finish();
  };

  const handleDismiss = async () => {
    setBusy(true);
    await dismissWhatsappPrompt();
    setBusy(false);
    // Hidden either way: a failed dismiss just means the card can return on the next load.
    finish();
  };

  return (
    <div dir="rtl" style={cardStyle} data-surface={surface}>
      <MessageCircle size={20} aria-hidden style={{ color: "var(--ys-primary)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={titleStyle}>
          {copyVariant === "onboarding" ? WHATSAPP_OPTIN_COPY.onboardingCardTitle : WHATSAPP_OPTIN_COPY.cardTitle}
        </p>
        <CardBody phoneMasked={maskPhone(option.phoneLast3)} copyVariant={copyVariant} />
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            style={{ ...acceptBtnStyle, opacity: busy ? 0.7 : 1 }}
            disabled={busy}
            onClick={() => void handleAccept()}
          >
            {WHATSAPP_OPTIN_COPY.accept}
          </button>
          <button type="button" style={ghostBtnStyle} disabled={busy} onClick={() => void handleDismiss()}>
            {WHATSAPP_OPTIN_COPY.dismiss}
          </button>
        </div>
      </div>
    </div>
  );
}
